import { invoke } from "@tauri-apps/api/core";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";

import type {
  PortfolioRepository,
  PortfolioRepositoryCommitResult,
  PortfolioRepositoryDeleteResult,
  PortfolioRepositoryReadResult,
  PortfolioRevision,
} from "../application/portfolioRepository";
import {
  migrateLocalPortfolioStorageEnvelope,
  validateLocalPortfolioRecordV1,
  type LocalPortfolioRecordV1,
} from "../domain";

const NATIVE_PORTFOLIO_CHANGED_EVENT = "portfolio-native-changed";

type NativeReadResult =
  | {
      readonly status: "ready";
      readonly payload: string;
      readonly revision: string;
    }
  | {
      readonly status: "empty";
    };

type NativeCommitResult =
  | {
      readonly status: "committed";
      readonly revision: string;
    }
  | {
      readonly status: "conflict";
      readonly current: NativeReadResult;
    };

type NativeDeleteResult =
  | { readonly status: "deleted" }
  | {
      readonly status: "conflict";
      readonly current: NativeReadResult;
    };

export interface TauriPortfolioBridge {
  invoke<T>(
    command: string,
    args?: Record<string, unknown>,
  ): Promise<T>;
  listen(
    event: string,
    handler: () => void,
  ): Promise<UnlistenFn>;
}

const DEFAULT_BRIDGE: TauriPortfolioBridge = {
  invoke,
  listen: (event, handler) => listen(event, handler),
};

function decodeNativeRead(
  result: NativeReadResult,
): PortfolioRepositoryReadResult {
  if (result.status === "empty") {
    return { status: "empty", revision: null, intentional: true };
  }

  try {
    const parsed: unknown = JSON.parse(result.payload);
    const validated = validateLocalPortfolioRecordV1(
      migrateLocalPortfolioStorageEnvelope(parsed),
    );
    if (!validated.success) {
      return { status: "blocked", reason: "invalid_record" };
    }
    return {
      status: "ready",
      record: validated.data,
      revision: result.revision,
    };
  } catch {
    return { status: "blocked", reason: "invalid_record" };
  }
}

export function createTauriPortfolioRepository(
  bridge: TauriPortfolioBridge = DEFAULT_BRIDGE,
): PortfolioRepository {
  return {
    async read() {
      try {
        const result = await bridge.invoke<NativeReadResult>(
          "portfolio_read",
        );
        return decodeNativeRead(result);
      } catch {
        return { status: "blocked", reason: "storage_read_failed" };
      }
    },

    async commit(
      record: LocalPortfolioRecordV1,
      expectedRevision: PortfolioRevision,
    ): Promise<PortfolioRepositoryCommitResult> {
      const validated = validateLocalPortfolioRecordV1(record);
      if (!validated.success) {
        return { status: "failed", reason: "invalid_record" };
      }

      try {
        const result = await bridge.invoke<NativeCommitResult>(
          "portfolio_commit",
          {
            expectedRevision,
            payload: JSON.stringify(validated.data),
          },
        );
        if (result.status === "conflict") {
          return {
            status: "conflict",
            current: decodeNativeRead(result.current),
          };
        }
        return {
          status: "committed",
          record: validated.data,
          revision: result.revision,
        };
      } catch {
        return { status: "failed", reason: "storage_write_failed" };
      }
    },

    async delete(
      expectedRevision: Exclude<PortfolioRevision, null>,
    ): Promise<PortfolioRepositoryDeleteResult> {
      try {
        const result = await bridge.invoke<NativeDeleteResult>(
          "portfolio_delete",
          { expectedRevision },
        );
        if (result.status === "conflict") {
          return {
            status: "conflict",
            current: decodeNativeRead(result.current),
          };
        }
        return {
          status: "deleted",
          revision: null,
          cleanupPending: false,
        };
      } catch {
        return { status: "failed", reason: "storage_write_failed" };
      }
    },

    subscribe(listener) {
      let disposed = false;
      let unlisten: UnlistenFn | undefined;
      void bridge
        .listen(NATIVE_PORTFOLIO_CHANGED_EVENT, listener)
        .then((nextUnlisten) => {
          if (disposed) {
            nextUnlisten();
          } else {
            unlisten = nextUnlisten;
          }
        })
        .catch(() => {
          // A failed event channel does not make direct reads and writes unsafe.
        });

      return () => {
        disposed = true;
        unlisten?.();
      };
    },
  };
}
